import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle, Shield, Heart,
  FileSearch, Users, Lock, ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './WhatWeOffer.css';

const products = [
  {
    icon: <CheckCircle size={28} />,
    badge: 'Most Popular',
    badgeClass: 'badge-purple',
    title: 'ClearSense',
    tagline: 'AI Answer Verification',
    description: 'Instantly verify AI-generated content for logical consistency, factual accuracy, and verifiable claims. Trust AI answers with confidence.',
    price: 'Free · Pro £19.99/mo',
    link: '/clearsense',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    accentColor: '#667eea',
    featured: true,
  },
  {
    icon: <Heart size={28} />,
    badge: 'Wellbeing',
    badgeClass: 'badge-green',
    title: 'SafePulse',
    tagline: 'Workforce Wellbeing & MFA',
    description: 'AI-powered mental health monitoring and multi-factor authentication. Protect and support your team from the inside out.',
    price: 'Contact for pricing',
    link: '/safepulse',
    gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    accentColor: '#11998e',
    featured: false,
  },
  {
    icon: <Shield size={28} />,
    badge: 'Certification',
    badgeClass: 'badge-blue',
    title: 'TrustMark',
    tagline: 'Digital Trust Certification',
    description: 'Certify your digital processes and build credibility with stakeholders through Erasys TrustMark — the standard for verified digital trust.',
    price: 'View pricing',
    link: '/TrustMark',
    gradient: 'linear-gradient(135deg, #0071e3 0%, #5ac8fa 100%)',
    accentColor: '#0071e3',
    featured: false,
  },
  {
    icon: <FileSearch size={28} />,
    badge: 'Intelligence',
    badgeClass: 'badge-amber',
    title: 'ClearFrame',
    tagline: 'Document Intelligence',
    description: 'Extract, analyse, and verify information from documents with precision. Turn unstructured content into structured intelligence.',
    price: 'Contact us',
    link: '/clearframe',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    accentColor: '#f093fb',
    featured: false,
  },
  {
    icon: <Lock size={28} />,
    badge: 'Security',
    badgeClass: 'badge-dark',
    title: 'Cybersecurity',
    tagline: 'Human-Centric Security',
    description: 'End-to-end cybersecurity solutions designed around your people. Manage risks, empower your team, and stay ahead of threats.',
    price: 'Contact us',
    link: '/cybersecurity',
    gradient: 'linear-gradient(135deg, #2e4bcc 0%, #4facfe 100%)',
    accentColor: '#2e4bcc',
    featured: false,
  },
  {
    icon: <Users size={28} />,
    badge: 'Consulting',
    badgeClass: 'badge-rose',
    title: 'HR Consulting',
    tagline: 'People & Culture Strategy',
    description: 'Strategic people consulting to align your workforce with business goals. Elevate culture, performance, and wellbeing together.',
    price: 'Contact us',
    link: '/Hrconsulting',
    gradient: 'linear-gradient(135deg, #f857a6 0%, #ff5858 100%)',
    accentColor: '#f857a6',
    featured: false,
  },
];

const WhatWeOffer = () => {
  const navigate = useNavigate();

  return (
    <section className="offer-section">
      <div className="offer-container">
        <motion.div
          className="offer-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="offer-eyebrow">The Erasys Ecosystem</span>
          <h2 className="section-title">Every Tool Your Business Needs</h2>
          <p className="section-subtitle">
            Six intelligent solutions. Built to work together.
          </p>
        </motion.div>

        {/* Featured ClearSense */}
        <motion.div
          className="offer-featured-card"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          onClick={() => navigate('/clearsense')}
        >
          <div className="offer-featured-left">
            <div className="offer-featured-badge">⭐ Most Popular</div>
            <div className="offer-featured-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <CheckCircle size={36} color="white" />
            </div>
            <h2 className="offer-featured-title">ClearSense</h2>
            <p className="offer-featured-tagline">AI Answer Verification</p>
            <p className="offer-featured-description">
              Don't just trust AI answers — verify them. ClearSense analyses AI-generated content for logical consistency, verifiable claims, and factual accuracy. Get instant insights with our free tier or unlock advanced source verification with Pro.
            </p>
            <div className="offer-featured-pricing">
              <span className="offer-price-free">Free</span>
              <span className="offer-price-divider">·</span>
              <span className="offer-price-pro">Pro £19.99/mo</span>
            </div>
            <button className="offer-featured-cta">
              Try ClearSense Free <ArrowRight size={18} />
            </button>
          </div>
          <div className="offer-featured-right">
            <div className="offer-featured-pills">
              {['Logic Verification', 'Claim Extraction', 'Source Verification', 'Trust Score', 'Instant Analysis', 'Unlimited Exports (Pro)'].map((pill, i) => (
                <span key={i} className="offer-pill">{pill}</span>
              ))}
            </div>
            <div className="offer-featured-mockup">
              <div className="mockup-bar">
                <span className="mockup-dot red" /><span className="mockup-dot yellow" /><span className="mockup-dot green" />
                <span className="mockup-url">clearsense.erasys.co.uk</span>
              </div>
              <div className="mockup-content">
                <div className="mockup-score">
                  <span className="mockup-score-number">82</span>
                  <span className="mockup-score-label">Trust Score</span>
                </div>
                <div className="mockup-tags">
                  <span className="mockup-tag tag-warning">2 Claims Detected</span>
                  <span className="mockup-tag tag-success">Logic ✓</span>
                  <span className="mockup-tag tag-info">Sources Verified</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Products Grid */}
        <div className="offer-grid">
          {products.slice(1).map((product, index) => (
            <motion.div
              key={index}
              className="offer-card"
              onClick={() => navigate(product.link)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
            >
              <div className="offer-card-top" style={{ background: product.gradient }}>
                <div className="offer-card-icon">{product.icon}</div>
                <span className={`offer-card-badge ${product.badgeClass}`}>{product.badge}</span>
              </div>
              <div className="offer-card-body">
                <h3 className="offer-card-title">{product.title}</h3>
                <p className="offer-card-tagline">{product.tagline}</p>
                <p className="offer-card-description">{product.description}</p>
                <div className="offer-card-footer">
                  <span className="offer-card-price">{product.price}</span>
                  <span className="offer-card-link" style={{ color: product.accentColor }}>
                    Learn more <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhatWeOffer;
